import { PillarPage } from 'src/constants/searchPages'
import { RedirectUrls } from 'src/types/types'

/**
 * Pure decisions behind the cookie banner — kept out of the component so they
 * can be unit-tested and so the "banner is not in the way" contract that arms
 * Google One Tap (`IAuthContext.cookiesDealt`) is spelled out in one place.
 */

/** Every `cookie_consent` fire carries one of these as `action`. */
export type CookieConsentAction =
  | 'shown'
  | 'accept'
  | 'reject'
  | 'save_preferences'
  | 'dismiss'

/**
 * sessionStorage flag written by the banner's X / Escape. Session-scoped on
 * purpose: no consent cookie is written on dismiss, so a NEW visit shows the
 * banner again, while SPA navigation inside this tab never re-shows it.
 * Read/written only through `safeStorage` — never touch sessionStorage raw.
 */
export const CONSENT_BANNER_DISMISSED_KEY = 'spotme:cookie-banner-dismissed'

/** Mirrors the Tailwind `md` screen (900px) — the first desktop breakpoint. */
export const DESKTOP_MIN_WIDTH_PX = 900

/**
 * Landing paths where the banner is deliberately suppressed. Suppression is a
 * conversion decision (host funnel hero, Flotillas, deep hood/listing intent)
 * and is NOT consent — the caller must still mark the banner as dealt with so
 * One Tap arms on these pages.
 */
export const shouldShowConsentBanner = (pathname: string): boolean => {
  // Always block /anfitrion/* — host funnel hero; banner would delay
  // first-touch supply lead capture.
  if (pathname.includes(RedirectUrls.Anfitrion)) return false

  // /flotillas is the highest-converting B2B parking surface (~32%
  // conversion). Keep banner off — friction here costs the most.
  if (pathname === '/pension-estacionamiento/flotillas') return false

  // Pillar paths — block only the deepest CONVERSION context:
  //   /[pillar]/[city]/[hood]            — hood search (deep intent)
  //   /[pillar]/[city]/[hood]/[configId] — listing detail (conversion)
  // Allow shallower paid-landing surfaces (pillar root, city pages, blog,
  // /almacenamiento-a-domicilio) so consent is captured BEFORE the user
  // navigates deep.
  const segments = pathname.split('/').filter(Boolean)
  const [pillarSeg, citySeg, hoodSeg] = segments
  const isPillarPath = (Object.values(PillarPage) as string[]).includes(
    pillarSeg
  )
  const isHoodOrListing =
    isPillarPath &&
    Boolean(citySeg) &&
    Boolean(hoodSeg) &&
    citySeg !== 'blog' &&
    citySeg !== 'almacenamiento-a-domicilio'
  return !isHoodOrListing
}

/**
 * Whether a SHOWING banner visually collides with Google One Tap. On desktop
 * (≥ `md`) the card is a bottom-left 420–460px card with no backdrop and One
 * Tap renders top-right — no collision, so One Tap can arm immediately. On
 * mobile the card covers the bottom of the viewport where One Tap's sheet
 * lands, so the gate waits for a decision or a dismiss.
 */
export const bannerCollidesWithOneTap = (viewportWidth: number): boolean =>
  viewportWidth < DESKTOP_MIN_WIDTH_PX
